import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { useLang } from "../context/LanguageContext";

const links = [
  { key: "home", to: "/" },
  { key: "vehicles", to: "/vehicules" },
  { key: "offers", to: "/offres" },
  { key: "reviews", to: "/avis" },
  { key: "contact", to: "/contact" },
];

export const Navbar = ({ variant = "dark" }) => {
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dark = variant === "dark";
  const solid = !dark || scrolled;
  const headerCls = dark
    ? (scrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/10" : "bg-transparent")
    : "bg-white/85 backdrop-blur-xl border-b border-[#071B2F]/10";
  const textBase = solid && !dark ? "text-[#071B2F]" : "text-white";
  const brandSub = dark ? "text-gold" : "text-[#D4AF37]";

  const linkCls = ({ isActive }) =>
    `overline text-xs transition-colors ${isActive ? "text-[#D4AF37]" : (dark ? "text-white/70 hover:text-gold" : "text-[#071B2F]/70 hover:text-[#D4AF37]")}`;

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${headerCls}`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <NavLink to="/" data-testid="brand-logo" className="flex flex-col items-start leading-none">
          <span className={`font-serif text-xl md:text-2xl tracking-tight ${textBase}`}>EBEN EZER</span>
          <span className={`overline text-[9px] ${brandSub}`}>Taxi VIP · Belgique</span>
        </NavLink>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink key={l.key} to={l.to} className={linkCls} data-testid={`nav-${l.key}`}>
              {t.nav[l.key]}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className={`overline text-xs border px-3 py-2 transition-colors ${dark && !scrolled ? "border-white/20 hover:border-gold" : "border-[#071B2F]/20 hover:border-[#D4AF37]"}`}
          >
            <span className={lang === "fr" ? "text-[#D4AF37]" : (solid && !dark ? "text-[#071B2F]/50" : "text-white/50")}>FR</span>
            <span className="text-[#9a9a9a] mx-1">/</span>
            <span className={lang === "nl" ? "text-[#D4AF37]" : (solid && !dark ? "text-[#071B2F]/50" : "text-white/50")}>NL</span>
          </button>
          <button
            onClick={() => navigate("/vehicules")}
            data-testid="nav-book-cta"
            className="hidden sm:inline-block overline text-xs text-black bg-gold hover:bg-gold-champagne px-5 py-3 transition-colors"
          >
            {t.nav.book}
          </button>
          <button className={`lg:hidden ${textBase}`} onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" aria-label="menu">
            {open ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} className="lg:hidden overflow-hidden bg-[#0A0A0A] border-t border-white/10">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink key={l.key} to={l.to} onClick={() => setOpen(false)} className="overline text-sm text-white/80" data-testid={`mobile-nav-${l.key}`}>
                {t.nav[l.key]}
              </NavLink>
            ))}
            <button onClick={() => { setOpen(false); navigate("/vehicules"); }} className="overline text-sm text-black bg-gold px-5 py-3 mt-2 text-center" data-testid="mobile-nav-book">
              {t.nav.book}
            </button>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};
